import { QRCodeSVG } from 'qrcode.react';
import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledDropdown,
} from 'reactstrap';
import { BaseUserWithAddress, isMinorAttendee, UserRegistration } from '../../../shared/user/base';
import { BaseCreateUser } from '../../../shared/user/create';
import {
  formatDate,
  getDefaultDate,
  LinkButton,
  MaterialIcon,
  RegisterSubUserInfo,
} from '../../components';
import { ActionModal } from '../../components/ActionModal';
import { JSONForm } from '../../components/json-form/JSONForm';
import { useTranslation } from '../../translations';
import { displayName, renderName, useConvention, useForm, useUserSafe } from '../../utils';
import { captureError } from '../../utils/errorHandling';
import { RaffleRegState } from './raffleregstate';

import './listitem.scss';

interface RegistrationListItemProps {
  readonly conRegistration?: UserRegistration;
  readonly user: BaseUserWithAddress;
  readonly refresh: () => void;
}

export const RegistrationListItem: FC<RegistrationListItemProps> = ({
  conRegistration: reg,
  user,
  refresh,
}) => {
  const { enableRegQR } = useConvention();

  const ownerUser = useUserSafe();
  const { ts } = useTranslation();
  const [showQR, setShowQR] = useState(false);
  const { startAt, childAgeThreshold } = useConvention();

  return (
    <>
      {enableRegQR && showQR && reg && (
        <RegQRModal
          close={() => {
            setShowQR(false);
          }}
          registration={reg}
        />
      )}
      <Card className="mb-10">
        <CardBody>
          <div
            style={{
              minWidth: '200px',
              position: 'absolute',
              right: '15px',
              textAlign: 'right',
              top: '15px',
            }}
          >
            <DropdownRegMenu refresh={refresh} reg={reg} user={user} />
          </div>
          <h4 style={{ marginRight: '30px' }}>{reg ? reg.badgeName : renderName(user)}</h4>
          <div className="badge-list compact" style={{ display: 'inline' }}>
            {user.id !== ownerUser.id && (
              <Badge color="primary" pill>
                {ts('common.id_number', user.id)}
              </Badge>
            )}

            {reg && (
              <Badge color="primary" pill>
                {displayName(reg.attendanceType)}
              </Badge>
            )}

            {reg?.paidOrderItem && (
              <Badge className="reg-state-paid" color="success" pill>
                {ts('common.paid')}
              </Badge>
            )}
            {reg && !reg.paidOrderItem && (
              <Badge className="reg-state-unpaid" color="secondary" pill>
                {ts('common.unpaid')}
              </Badge>
            )}
            {!reg && (
              <Badge className="reg-state-unpaid" color="secondary" pill>
                {ts('no_registration')}
              </Badge>
            )}
            {(user.child ||
              (user.bornAt && isMinorAttendee(user.bornAt, startAt, childAgeThreshold))) && (
              <Badge className="reg-state-child" color="warning" pill>
                {ts('common.child')}
              </Badge>
            )}
          </div>
          <div className="float-end">
            <div className="hstack gap-2">
              {enableRegQR && reg && (
                <Button
                  color="secondary"
                  id={`qr-${reg.id}`}
                  onClick={() => {
                    setShowQR(true);
                  }}
                  outline
                >
                  <MaterialIcon name="qr_code" small />
                </Button>
              )}
              <LinkButton
                color="primary"
                id={`editRegistration${user.id}`}
                outline
                to={`/event/register/edit/${user.id}`}
              >
                {ts(reg ? 'modify_registration' : 'common.register')}
              </LinkButton>
            </div>
          </div>
          {reg?.attendanceType.raffleId && !reg.paidOrderItem && (
            <RaffleRegState raffleId={reg.attendanceType.raffleId} />
          )}
        </CardBody>
      </Card>
    </>
  );
};

interface RegQRModalProps {
  readonly registration: UserRegistration;
  readonly close: () => void;
}

const RegQRModal: FC<RegQRModalProps> = ({ registration, close }) => {
  const { ts } = useTranslation();

  const uri = `@CONCAT;UUID${registration.uuid}`;
  return (
    <Modal id="qrModal" isOpen>
      <ModalHeader>{ts('qr.user.modal.title', registration.badgeName)}</ModalHeader>
      <ModalBody>
        <QRCodeSVG className="w-100" value={uri} />
      </ModalBody>
      <ModalFooter>
        <Button onClick={close}>{ts('common.close')}</Button>
      </ModalFooter>
    </Modal>
  );
};

interface DropdownRegMenuProps {
  readonly user: BaseUserWithAddress;
  readonly reg?: UserRegistration;
  readonly refresh: () => void;
}

const DropdownRegMenu: FC<DropdownRegMenuProps> = ({ user, reg, refresh }) => {
  const [modal, setModal] = useState<'transfer' | 'edit' | 'delete' | null>(null);
  const { ts } = useTranslation();
  const { enableRegTransfer } = useConvention();
  const localUser = useUserSafe();
  const items = [];
  const close = useCallback(() => {
    setModal(null);
  }, [setModal]);

  if (reg?.paidOrderItem && enableRegTransfer) {
    items.push(
      <DropdownItem
        className="registration-action-transfer"
        key="regTransfer"
        onClick={() => {
          setModal('transfer');
        }}
      >
        {ts('transfer_registration')}
      </DropdownItem>,
    );
  }

  if (reg && !reg.paidOrderItem) {
    items.push(
      <React.Fragment key="delete">
        {items.length > 0 && <hr />}
        <DropdownItem
          className="text-danger registration-action-delete"
          onClick={async () =>
            await api.deleteRegistration(reg.id).then(refresh).catch(captureError)
          }
        >
          {ts('common.delete')} {ts('registration')}
        </DropdownItem>
      </React.Fragment>,
    );
  }

  if (user.id !== localUser.id) {
    if (!user.child) {
      items.push(
        <DropdownItem
          className="action-edit-personal-details"
          key="regEditPersonalDetails"
          onClick={() => {
            setModal('edit');
          }}
        >
          {ts('common.edit_personal_details')}
        </DropdownItem>,
      );
    }

    items.push(
      <DropdownItem
        className="text-danger action-delete-sub"
        key="regDeleteSub"
        onClick={() => {
          setModal('delete');
        }}
      >
        {ts('user.subuser.deletion.title')}
      </DropdownItem>,
    );
  }

  return (
    <>
      {modal === 'transfer' && reg && (
        <Modal className="transfer-registration" isOpen toggle={close}>
          <TransferRegistrationComponent
            close={close}
            conRegistration={reg}
            refresh={refresh}
            user={user}
          />
        </Modal>
      )}
      {modal === 'edit' && (
        <EditPersonalDetailsComponent close={close} refresh={refresh} user={user} />
      )}
      {modal === 'delete' && (
        <ActionModal
          actionContent="user.subuser.deletion.title"
          close={close}
          color="danger"
          id="deleteAccount"
          isOpen
          onComplete={async () => {
            await api.deleteUser(user.id);
            toast.success('Account deleted');
            setModal(null);
            refresh();
          }}
          title="user.subuser.deletion.title"
        >
          {ts('user.deletion.confirmation')}
        </ActionModal>
      )}
      <UncontrolledDropdown className="mw-100">
        {items.length > 0 && (
          <DropdownToggle id={`regDropdown${user.id}`} tag="a">
            <MaterialIcon className="action-more-options clickable" name="more_horiz" />
          </DropdownToggle>
        )}
        <DropdownMenu end>{items}</DropdownMenu>
      </UncontrolledDropdown>
    </>
  );
};

interface TransferRegistrationComponentProps {
  readonly user: BaseUserWithAddress;
  readonly conRegistration: UserRegistration;
  readonly close: () => void;
  readonly refresh: () => void;
}

const TransferRegistrationComponent: FC<TransferRegistrationComponentProps> = ({
  user,
  conRegistration: reg,
  close,
  refresh,
}) => {
  const { ts, t } = useTranslation();
  const { badgePrinted } = reg;

  if (badgePrinted) {
    return (
      <>
        <ModalHeader>{ts('transfer_registration')}</ModalHeader>
        <ModalBody>
          <div className="reg-transfer-error reg-transfer-already-printed">
            <div>
              <MaterialIcon medium name="front_hand" type="danger" />
            </div>
            <div>
              <p>
                <strong>{ts('this_registration_cannot_be_transferred')}</strong>
              </p>
              <p>{ts('if_you_have_any_questions')}</p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" id="cancel" onClick={close} outline>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <JSONForm
      method="post"
      onSuccess={() => {
        toast.success(ts('registration_has_been_transferred'));
        close();
        refresh();
      }}
      path={`/api/registrations/${user.id}/transfer`}
    >
      <ModalHeader>{ts('transfer_registration')}</ModalHeader>
      <ModalBody>
        <p>{ts('you_can_only_transfer_a')}</p>
        <p>{t('reg.transfer.complex_desc', <strong>{ts('permanent')}</strong>)}</p>
        <hr />

        <Row>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="email">{ts('target_email')}</Label>
              <Input
                id="email"
                name="email"
                placeholder={ts('email_of_intended_recipient')}
                type="email"
              />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="password">{ts('common.password')}</Label>
              <Input id="password" name="password" type="password" />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="danger" id="transferButton">
          {ts('transfer_registration')}
        </Button>{' '}
        <Button color="secondary" id="cancel" onClick={close} outline>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </JSONForm>
  );
};

interface EditPersonalDetailsProps {
  readonly user: BaseUserWithAddress;
  readonly close: () => void;
  readonly refresh: () => void;
}

const EditPersonalDetailsComponent: FC<EditPersonalDetailsProps> = ({
  user: inputUser,
  close,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [user, setUser] = useState<BaseCreateUser>({
    firstName: inputUser.firstName!,
    lastName: inputUser.lastName!,
    bornAt: inputUser.bornAt ? formatDate(inputUser.bornAt) : getDefaultDate(),
    newsletter: false,
    preferredName: inputUser.preferredName ?? '',
    phone: inputUser.phone,
    email: inputUser.email,
    addressCity: inputUser.addressCity,
    addressCountry: inputUser.addressCountry,
    addressLine1: inputUser.addressLine1,
    addressLine2: inputUser.addressLine2,
    addressState: inputUser.addressState,
    addressZipcode: inputUser.addressZipcode,
  });

  const form = useForm(async () => {
    let bornAt: Date | undefined;

    if (user.bornAt) {
      bornAt = new Date(user.bornAt);

      if (Number.isNaN(bornAt.getTime())) {
        toast.error(`Invalid date: ${user.bornAt}`);
        return;
      }
    }

    await api.updateUser(inputUser.id, { ...user, bornAt });
    toast.success('Personal details updated');
    refresh();
    close();
  }, [close, inputUser.id, refresh, user]);

  return (
    <Modal className="modal-large" id="editPersonalDetails" isOpen toggle={close}>
      <form onSubmit={form.onSubmit}>
        <ModalHeader>{ts('common.edit_personal_details')}</ModalHeader>
        <ModalBody>
          <RegisterSubUserInfo setUser={setUser} user={user} />
        </ModalBody>
        <ModalFooter>
          <Button
            className="action-confirm"
            color="primary"
            disabled={form.saving}
            id="submitEdit"
            type="submit"
          >
            {ts('common.update')}
          </Button>
          <Button color="secondary" id="cancel" onClick={close}>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
